import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { progressService } from "../../../../../services/progressService";
import { FaChevronCircleUp } from "react-icons/fa";

import Lion from "../../../../../assets/animals/Lion.png";
import Monkey from "../../../../../assets/animals/Monkey.png";
import Fox from "../../../../../assets/animals/Fox.png";
import Cat from "../../../../../assets/animals/Cat.png";
import Koala from "../../../../../assets/animals/Koala.png";

const iconMap = { lion: Lion, monkey: Monkey, fox: Fox, cat: Cat, koala: Koala };

/* ---------------- Header Band Component ---------------- */
const HeaderBand = ({ color, subtitle, title }) => (
  <div
    className="rounded-t-[11px] px-4 pt-3 pb-4 text-center"
    style={{ backgroundColor: color }}
  >
    <div className="text-[10px] tracking-[0.06em] font-semibold uppercase text-white/85">
      {subtitle}
    </div>
    <div className="mt-1 text-[16px] font-semibold text-white">
      {title}
    </div>
  </div>
);

/* ---------------- Main Component ---------------- */
const StakeholderPrioritySummary = ({ userId, subsectionId, data = {}, onNextStep }) => {
  const [visibilityMap, setVisibilityMap] = useState(data?.visibilityMap || {});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadSavedMap() {
      try {
        setLoading(true);
        const progress = await progressService.getUserProgress(userId, subsectionId);
        const savedMap = progress?.data?.visibilityMap;
        if (savedMap && Object.keys(savedMap).length > 0) {
          setVisibilityMap(savedMap);
        }
      } catch (err) {
        console.error("Failed to load saved visibility map:", err);
      } finally {
        setLoading(false);
      }
    }
    if (!data?.visibilityMap || Object.keys(data.visibilityMap).length === 0) {
      loadSavedMap();
    }
  }, [userId, subsectionId, data?.visibilityMap]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px] text-gray-500">
        Loading stakeholder summary...
      </div>
    );
  }

  const summaryData = {
    start_connecting: visibilityMap.q1?.items || [],
    prioritize_nurture: visibilityMap.q2?.items || [],
    keep_in_touch: visibilityMap.q4?.items || [],
    deprioritise: visibilityMap.q3?.items || [],
  };

  const sections = [
    {
      key: "start_connecting",
      title: "Start Connecting, Build Relation",
      subtitle: "WITH THESE PEOPLE, YOU SHOULD",
      color: "#ED254E",
      priority: "TOP PRIORITY",
      description:
        "These stakeholders have high influence but lack visibility into your actions, tasks, and achievements.",
    },
    {
      key: "prioritize_nurture",
      title: "Prioritize & Nurture Connection",
      subtitle: "WITH THESE PEOPLE, YOU SHOULD",
      color: "#F9A825",
      priority: "NEXT PRIORITY",
      description:
        "These stakeholders have high influence. You have decent influence and visibility. Keep them engaged with updates about you and your work.Their advocacy is crucial for you.",
    },
    {
      key: "keep_in_touch",
      title: "Keep in Touch Occasionally",
      subtitle: "WITH THESE PEOPLE, YOU SHOULD",
      color: "#6F6F6F",
    },
    {
      key: "deprioritise",
      title: "Deprioritise",
      subtitle: "WITH THESE PEOPLE, YOU SHOULD",
      color: "#505050",
    },
  ];

  const getPeople = (key) => summaryData[key] || [];

  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-[#F8F8F8] px-6 py-16">
      <h2 className="text-[22px] font-semibold text-[#1C1C1C] mb-12 text-center">
        In simple terms, here’s what you should prioritise:
      </h2>

      {/* Grid layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[392px_392px_392px] gap-8 max-w-[1220px]">
        {/* === Left two tall cards === */}
        {sections.slice(0, 2).map((section, idx) => (
          <motion.div
            key={section.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="relative flex flex-col justify-between bg-white rounded-[11px] border border-[#CCCCCC] shadow-sm"
            style={{ width: "392px", height: "588px" }}
          >
            <HeaderBand
              color={section.color}
              subtitle={section.subtitle}
              title={section.title}
            />

            {/* People list */}
            <div className="flex flex-col items-center justify-start py-4 px-5 gap-3">
              {getPeople(section.key).length > 0 ? (
                getPeople(section.key).map((person, i) => (
                  <div
                    key={`${person.name}-${i}`}
                    className="w-full rounded-lg border border-[#E0E0E0] bg-white p-3 flex items-center gap-3 shadow-[0_1px_0_rgba(0,0,0,0.03)]"
                  >
                    <img
                      src={person.animal || iconMap[person.icon?.toLowerCase()] || Koala}
                      alt={person.name}
                      className="w-7 h-7 object-contain"
                    />
                    <div className="flex flex-col text-left leading-tight">
                      <span className="font-semibold text-[#1C1C1C] text-[14px]">
                        {person.name}
                      </span>
                      <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                        {person.relation || person.role}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-sm italic py-4">
                  No stakeholders in this group
                </p>
              )}
            </div>

            {/* Watermark + Description */}
            <div className="px-4 pb-4 mt-auto relative">
              {section.priority && (
                <div className="absolute left-6 bottom-[110px] text-[28px] font-extrabold tracking-wide uppercase text-black/10 select-none">
                  {section.priority}
                </div>
              )}
              {section.description && (
                <div className="bg-[#F1F1F1] text-[#6C6C6C] text-[11px] px-4 py-3 border border-[#E0E0E0] rounded-md flex items-start gap-2">
                  <Info className="w-4 h-4 mt-[2px]" />
                  <span>{section.description}</span>
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* === Right column smaller cards (aligned bottom) === */}
        <div
          className="flex flex-col justify-between"
          style={{ height: "588px", width: "392px" }}
        >
          {sections.slice(2).map((section, idx) => (
            <motion.div
              key={section.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + idx * 0.1 }}
              className="flex flex-col bg-white rounded-[11px] border border-[#CCCCCC] shadow-sm"
              style={{ width: "392px", height: "250px" }}
            >
              <HeaderBand
                color={section.color}
                subtitle={section.subtitle}
                title={section.title}
              />

              <div className="flex flex-col items-center justify-center py-4 px-5 gap-3">
                {getPeople(section.key).length > 0 ? (
                  getPeople(section.key).map((person, i) => (
                    <div
                      key={`${person.name}-${i}`}
                      className="w-full rounded-lg border border-[#E0E0E0] bg-white p-3 flex items-center gap-3"
                    >
                      <img
                        src={person.animal || iconMap[person.icon?.toLowerCase()] || Koala}
                        alt={person.name}
                        className="w-7 h-7 object-contain"
                      />
                      <div className="flex flex-col text-left leading-tight">
                        <span className="font-semibold text-[#1C1C1C] text-[14px]">
                          {person.name}
                        </span>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                          {person.relation || person.role}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm italic py-4">
                    No stakeholders in this group
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* === CTA BUTTON === */}
      <button
        onClick={onNextStep}
        className="mt-12 flex items-center justify-between w-[368px] h-[69px] rounded-[20px] px-[20px] py-[22px] bg-[#54B0AF] hover:bg-teal-600 text-white font-gotham font-medium text-[16px] lg:text-[19px] 2xl:text-[21px] transition-all"
      >
        <span>Next: Promotion Process</span>
        {/* <ArrowRight className="w-5 h-5" /> */}
        <FaChevronCircleUp className="rotate-90 ml-2" />
      </button>
    </div>
  );
};

export default StakeholderPrioritySummary;
